import React, { useState } from "react";

import { useLocation } from "react-router-dom";

import { ReportProps } from "../../..";
import { LoadingCost } from "../../../../../../../../../models/loading.cost";
import { ColumnWithKey } from "../../../../../../../../../types";
import { CronJobType } from "../../../../../../../../../util/enums";
import { useActionReportMaintenance } from "../../../hooks/useAction";
import { useReportState } from "../../../hooks/useReportState";
import { getRelatedFilters } from "../../../utils";
import { BaseReportGrid } from "../../BaseReportGrid";
import { useInvoiceColumns } from "./hooks/useInvoiceColumns";
import { useInvoicesFilters } from "./hooks/useInvoiceFilters";

export const InvoiceReport = () => {
  const { getAllInvoices, getAllLCRData } = useActionReportMaintenance();
  const {
    filters,
    setFilters,
    tempFilters,
    setTempFilters,
    allCurrentFilters,
    type,
    dateFilters,
    setDateFilters,
  } = useReportState(true);
  const [remoteData, setRemoteData] = useState<LoadingCost[]>([]);
  const { getInvoiceReportFilters } = useInvoicesFilters({ data: remoteData });
  const { useGenerateReport } = useActionReportMaintenance();
  const { mutate: generateReport, isLoading: loadingReport } = useGenerateReport();
  const { getInvoiceReportColumns } = useInvoiceColumns();
  const { state } = useLocation<ReportProps>();
  const { name: reportName } = state || {};

  const invoiceReportInclude = [
    {
      association: "invoiceShipment",
      include: [
        {
          required: true,
          association: "purchaseOrders",
          include: [
            {
              required: true,
              association: "purchaseOrderProducts",
              attributes: ["brandName", "quantity", "purchasePrice"],
              include: [
                {
                  required: true,
                  association: "product",
                  attributes: ["styleColor", "style"],
                },
              ],
            },
            {
              required: true,
              association: "originPort",
              attributes: ["siteCode"],
              include: [{ association: "country", attributes: ["code", "name"] }],
            },
            {
              required: true,
              attributes: ["siteCode", "locationName"],
              association: "destinationPort",
              include: [{ association: "country", attributes: ["code", "name"] }],
            },
            {
              required: true,
              association: "bookings",
              attributes: ["id"],
              include: [
                {
                  required: true,
                  association: "commercialInvoice",
                  attributes: ["invoiceNumber", "expDate"],
                },
                ...(type === CronJobType.SHIPMENT_COST_REPORT
                  ? [
                      { required: true, association: "consignee" },
                      {
                        required: true,
                        association: "portOfDischarge",
                        attributes: ["siteCode", "locationName"],
                        include: [{ association: "country", attributes: ["code", "name"] }],
                      },
                      {
                        required: true,
                        association: "portOfLoading",
                        attributes: ["siteCode"],
                        include: [{ association: "country", attributes: ["code", "name"] }],
                      },
                      {
                        required: true,
                        attributes: ["siteCode", "locationName"],
                        association: "portOfDestination",
                        include: [{ association: "country", attributes: ["code", "name"] }],
                      },
                    ]
                  : []),
              ],
            },
          ],
        },
      ],

      where: getRelatedFilters(allCurrentFilters, "invoiceShipment"),
    },
  ];

  const reportInclude = type !== CronJobType.LCR_REPORT ? invoiceReportInclude : [];

  const handleGenerateReport = (csvFields: string[]) => {
    setFilters(allCurrentFilters);

    const data = {
      reportName: reportName ?? "",
      reportGroup: state?.reportGroup?.subGroup ?? "",
      csvFields,
    };

    generateReport({
      data,
      filters: allCurrentFilters,
      include: reportInclude,
    });
  };

  const onChangeData = (value: any) => {
    setRemoteData(value);
  };

  return (
    <>
      <BaseReportGrid
        getAllData={type === CronJobType.LCR_REPORT ? getAllLCRData : getAllInvoices}
        columns={getInvoiceReportColumns(type) as ColumnWithKey[]}
        filterCategories={getInvoiceReportFilters(type)}
        filters={filters}
        setFilters={setFilters}
        reportInclude={reportInclude}
        setTempFilters={setTempFilters}
        tempFilters={tempFilters}
        dateFilters={dateFilters}
        setDateFilters={setDateFilters}
        allCurrentFilters={allCurrentFilters}
        onChangeData={type === CronJobType.LCR_REPORT ? onChangeData : undefined}
        showSelection={false}
        keysToexcludeFromLocalFilter={["dateRange"]}
        handleGenerateReport={handleGenerateReport}
        sendMailIncludes={invoiceReportInclude}
        generatingReportCustom={loadingReport}
        showSendMail
      />
    </>
  );
};
